import { camelCase } from 'lodash-es'
import { FieldValues, Path, UseFormSetError } from 'react-hook-form'
import { ComboboxItem } from '@node-space/ui-components'
import { Currencies, ErrorDetails } from 'types/types'
import { getTranslation } from '../../../../__mocks__/i18nextMock'
import { BUSINESS_CUSTOMER_ERROR_MAPPING, RISK_SCORES, USE_CASES } from './constants'
import {
  BusinessCustomerFormData,
  BusinessCustomerFormKey,
  EmbeddedCustomerBaseData,
  IndividualCustomerFormData,
  IndividualCustomerFormKey,
  UseCase,
} from './types'

export type FormKeyMapping<MappingType> = Partial<Record<IndividualCustomerFormKey, MappingType>>

export const BASE_CUSTOMER_DEFAULT_FORM_DATA: EmbeddedCustomerBaseData = {
  useCase: '' as UseCase,
  externalReference: '',
}

export const BUSINESS_CUSTOMER_DEFAULT_FORM_DATA: BusinessCustomerFormData = {
  businessName: '',
  businessDescription: '',
  industry: '',
  entityType: '',
  countryOfIncorporation: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  stateCode: '',
  monthlyExpectedVolumes: '',
  taxNumber: '',
  registrationNumber: '',
  isRegulated: '',
  regulatorName: '',
  regulatorJurisdiction: '',
  isPubliclyListed: '',
  listedExchange: '',
  tickerSymbol: '',
  isOperationalAddressDifferent: '',
  operationalAddressLine1: '',
  operationalAddressLine2: '',
  operationalAddressCity: '',
  operationalAddressPostalCode: '',
  operationalAddressCountryCode: '',
  operationalAddressStateCode: '',
  ...BASE_CUSTOMER_DEFAULT_FORM_DATA,
}

export const BASE_CUSTOMER_OPTIONS: FormKeyMapping<ComboboxItem[]> = {
  riskScore: Object.values(RISK_SCORES)?.map(riskScore => ({
    leftText: getTranslation(riskScore?.toLowerCase()),
    value: riskScore,
  })),
  useCase: Object.values(USE_CASES)?.map(useCase => ({
    leftText: useCase ? getTranslation(`wallets.${camelCase(useCase)}`) : '',
    value: useCase,
  })),
}

export const INDIVIDUAL_CURRENCIES = [Currencies.USD, Currencies.EUR]

export const INDIVIDUAL_DESCRIPTION = {
  transfersOwnWallet: 'TRANSFERS_OWN_WALLET',
  transfersFamilyAndFriends: 'TRANSFERS_FAMILY_FRIENDS',
  investments: 'INVESTMENTS',
  goodsServicesPurchases: 'GOODS_SERVICES',
  donations: 'DONATIONS',
}

export const INDIVIDUAL_EMPLOYMENT_STATUS = {
  selfEmployedFreelancer: 'SELF_EMPLOYED',
  salaried: 'SALARIED',
  student: 'STUDENT',
  unemployed: 'UNEMPLOYED',
  retired: 'RETIRED',
}

// NOT_PROVIDED is no longer selectable but is still persisted on customers created before ONB-4885
export const INDIVIDUAL_EMPLOYMENT_STATUS_LABELS = {
  ...INDIVIDUAL_EMPLOYMENT_STATUS,
  notProvided: 'NOT_PROVIDED',
}

export const INDIVIDUAL_PEP_STATUS = {
  notAPep: 'NOT_PEP',
  formerInactiveWithinTwoYears: 'FORMER_PEP_2_YEARS',
  formerInactiveOlderTwoYears: 'FORMER_PEP_OLDER',
  domestic: 'DOMESTIC_PEP',
  foreign: 'FOREIGN_PEP',
  closeAssociates: 'CLOSE_ASSOCIATES',
  familyMembers: 'FAMILY_MEMBERS',
}

export const INDIVIDUAL_SOURCE_OF_FUNDS = {
  salary: 'SALARY',
  pension: 'PENSION',
  savingsInvestments: 'SAVINGS',
  selfEmployed: 'SELF_EMPLOYMENT',
  cryptoTrading: 'CRYPTO_TRADING',
  gambling: 'GAMBLING',
  realEstate: 'REAL_ESTATE',
}

export const INDIVIDUAL_CUSTOMER_DEFAULT_FORM_DATA: IndividualCustomerFormData = {
  ...BASE_CUSTOMER_DEFAULT_FORM_DATA,
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  emailAddress: '',
  description: '',
  nationality: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  countryCode: '',
  stateCode: '',
  taxIdentificationNumber: '',
  taxResidenceCountryCode: '',
  expectedMonthlyVolumeAmount: '',
  expectedMonthlyVolumeCurrency: INDIVIDUAL_CURRENCIES?.[0],
  employmentStatus: '',
  sourceOfFunds: '',
  pepStatus: '',
  intendedUseOfAccount: '',
  estimatedYearlyIncome: '',
  employmentIndustrySector: '',
}

export const BASE_CUSTOMER_LABEL_MAPPING: Partial<Record<keyof EmbeddedCustomerBaseData, string>> =
  {
    useCase: 'customers.serviceType',
    externalReference: 'customers.externalReference',
  }

export const INDIVIDUAL_CUSTOMER_LABEL_MAPPING: FormKeyMapping<string> = {
  ...BASE_CUSTOMER_LABEL_MAPPING,
  emailAddress: 'formFields.email',
  nationality: 'customers.countryOfNationality',
  postalCode: 'customers.address.postalCode',
  countryCode: 'customers.address.country',
  stateCode: 'customers.address.state',
  taxIdentificationNumber: 'customers.taxIdentification',
  taxResidenceCountryCode: 'customers.taxResidency',
  expectedMonthlyVolumeAmount: 'customers.monthlyVolumes',
  expectedMonthlyVolumeCurrency: 'formFields.currency',
  employmentStatus: 'customers.employmentStatus',
  pepStatus: 'customers.pepStatus',
  sourceOfFunds: 'customers.sourceOfFunds',
  intendedUseOfAccount: 'customers.intendedUse',
  description: 'customers.descriptionField',
  estimatedYearlyIncome: 'customers.estimatedYearlyIncome',
  employmentIndustrySector: 'customers.employmentIndustrySector',
}

export const BUSINESS_CUSTOMER_LABEL_MAPPING: Partial<Record<BusinessCustomerFormKey, string>> = {
  ...BASE_CUSTOMER_LABEL_MAPPING,
  addressLine1: 'embeddedCustomers.businessForm.addressLine1',
  addressLine2: 'embeddedCustomers.businessForm.addressLine2',
  city: 'embeddedCustomers.businessForm.city',
  postalCode: 'embeddedCustomers.businessForm.postalCode',
  isRegulated: 'embeddedCustomers.businessForm.isRegulated',
  regulatorName: 'embeddedCustomers.businessForm.regulatorName',
  regulatorJurisdiction: 'embeddedCustomers.businessForm.regulatorJurisdiction',
  isPubliclyListed: 'embeddedCustomers.businessForm.isPubliclyListed',
  listedExchange: 'embeddedCustomers.businessForm.listedExchange',
  tickerSymbol: 'embeddedCustomers.businessForm.tickerSymbol',
  operationalAddressLine1: 'embeddedCustomers.businessForm.operationalAddressLine1',
  operationalAddressLine2: 'embeddedCustomers.businessForm.operationalAddressLine2',
  operationalAddressCity: 'embeddedCustomers.businessForm.operationalAddressCity',
  operationalAddressPostalCode: 'embeddedCustomers.businessForm.operationalAddressPostalCode',
  operationalAddressCountryCode: 'embeddedCustomers.businessForm.operationalAddressCountryCode',
}

export const mapBusinessErrorKeyToFormKey = (errorKey: string): BusinessCustomerFormKey => {
  const mappedKey = BUSINESS_CUSTOMER_ERROR_MAPPING[errorKey]
  if (!mappedKey) {
    return errorKey as BusinessCustomerFormKey
  }
  return mappedKey
}

export const setFieldErrors = <
  TFieldValues extends FieldValues = FieldValues,
  FormKey extends string = string,
>(
  errors: ErrorDetails,
  getErrorFormKey: (requestErrorKey: string) => FormKey,
  setError: UseFormSetError<TFieldValues>
): void => {
  if (!errors) return
  Object.entries(errors).forEach(([requestErrorKey, messages]) => {
    const formKey = getErrorFormKey(requestErrorKey)
    const errorMessage = typeof messages === 'string' ? messages : messages[0]
    setError(formKey as unknown as Path<TFieldValues>, {
      type: 'manual',
      message: errorMessage,
    })
  })
}
